import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import AdminPage from '../../../components/admin/AdminPage';
import PendingApproval from './PendingApproval';
import Approved from './Approved';
import Rejected from './Rejected';

function ApprovalRequest() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'pending';
  const [activeTab, setActiveTab] = useState(tabParam);
  const [searchTerm, setSearchTerm] = useState('');
  const refreshFnRef = useRef(null);

  useEffect(() => {
    if (tabParam && ['pending', 'approved', 'rejected'].includes(tabParam)) {
      setActiveTab(tabParam);
    }
  }, [tabParam]);

  const setActiveTabAndUrl = (tabId) => {
    setActiveTab(tabId);
    setSearchParams({ tab: tabId });
  };

  const registerRefresh = (fn) => {
    refreshFnRef.current = fn;
  };

  const handleGlobalRefresh = () => {
    if (refreshFnRef.current) {
        refreshFnRef.current();
    } else {
        window.location.reload();
    }
  };

  return (
    <AdminPage noPadding>
      <div className="w-full max-w-[1440px] mx-auto px-6 pt-0 pb-12 animate-in fade-in duration-700">
        
        {/* Modern Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 pb-8 border-b border-gray-100 gap-6">
            <div className="space-y-1">
                <div className="flex items-center gap-2 text-[#7083FF] font-bold text-xs uppercase tracking-[0.2em] mb-2">
                    <Icon icon="fluent:document-ribbon-24-regular" width={16} />
                    <span>Resource Governance</span>
                </div>
                <h1 className="text-4xl font-black text-gray-900 tracking-tight">Course Quality Assurance</h1>
                <p className="text-gray-500 font-medium max-w-lg">
                    Review and manage course submissions from trainers. Ensure all content meets platform quality standards before going live.
                </p>
            </div>

            <div className="flex items-center gap-3">
                {/* Global Refresh Action */}
                <button 
                    type="button"
                    onClick={handleGlobalRefresh}
                    className="p-3 rounded-2xl bg-white border border-gray-100 text-gray-400 hover:text-[#7083FF] hover:bg-[#7083FF]/5 transition-all shadow-sm group/refresh"
                    title="Refresh Workspace"
                >
                    <Icon icon="fluent:arrow-sync-24-regular" width={20} className="group-active/refresh:rotate-180 transition-transform duration-500" />
                </button>

                {/* Tab Navigation Hub - Premium Floating Style */}
                <div className="p-1.5 bg-gray-50 rounded-[28px] border border-gray-100 flex flex-wrap gap-1 items-center shadow-sm">
                    {[
                        { id: 'pending', label: 'Pending Review', icon: 'fluent:document-timer-24-regular' },
                        { id: 'approved', label: 'Approved', icon: 'fluent:checkmark-circle-24-regular' },
                        { id: 'rejected', label: 'Delisted', icon: 'fluent:dismiss-circle-24-filled' },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTabAndUrl(tab.id)}
                            className={`relative flex items-center gap-2.5 px-6 py-3.5 rounded-[22px] text-sm font-bold transition-all duration-500 group ${
                                activeTab === tab.id 
                                ? 'bg-white text-gray-900 shadow-xl shadow-gray-200/50 ring-1 ring-black/[0.02]' 
                                : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                            }`}
                        >
                            <Icon 
                                icon={tab.icon} 
                                width={20} 
                                height={20} 
                                className={`transition-all duration-500 ${activeTab === tab.id ? 'text-[#7083FF] scale-110' : 'text-gray-300 group-hover:text-gray-400'}`} 
                            />
                            <span className="whitespace-nowrap">{tab.label}</span>
                            {activeTab === tab.id && (
                                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-4 h-1 bg-[#7083FF] rounded-full blur-[2px]" />
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </div>

        {/* Dynamic Search & Stats Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-6 mb-10">
            {/* Search Hub */}
            <div className="relative flex-1 group/search">
                <div className="absolute left-1 top-1 bottom-1 w-11 rounded-2xl flex items-center justify-center transition-colors group-focus-within/search:bg-[#7083FF]/5">
                    <Icon 
                        icon="fluent:search-24-regular" 
                        className="text-gray-400 group-focus-within/search:text-[#7083FF] transition-all" 
                        width={20} 
                    />
                </div>
                <input
                    type="text"
                    placeholder="Search by course name, instructor, or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border border-gray-100 rounded-3xl pl-14 pr-6 py-4 text-sm font-medium text-gray-900 placeholder:text-gray-300 focus:outline-none focus:ring-4 focus:ring-[#7083FF]/5 focus:border-[#7083FF]/30 transition-all shadow-sm"
                />
                {searchTerm && (
                    <button 
                        onClick={() => setSearchTerm('')}
                        className="absolute right-4 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-gray-300 hover:text-gray-500 hover:bg-gray-50 transition-all"
                    >
                        <Icon icon="fluent:dismiss-16-regular" />
                    </button>
                )}
            </div>
        </div>

        {/* Tab content with smooth transition */}
        <div className="min-h-[600px] relative">
          {activeTab === 'pending' && <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 ease-out fill-mode-forwards"><PendingApproval searchTerm={searchTerm} setSearchTerm={setSearchTerm} registerRefresh={registerRefresh} /></div>}
          {activeTab === 'approved' && <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 ease-out fill-mode-forwards"><Approved searchTerm={searchTerm} /></div>}
          {activeTab === 'rejected' && <div className="animate-in fade-in slide-in-from-bottom-8 duration-700 ease-out fill-mode-forwards"><Rejected searchTerm={searchTerm} /></div>}
        </div>

        <style>{`
          .admin-page { padding: 0 !important; }
        `}</style>
      </div>
    </AdminPage>
  );
}

export default ApprovalRequest;
